import { FormEvent, useEffect, useState } from 'react';
import AdminLayout from '../AdminLayout';
import {
  createCertification,
  deleteCertification,
  fetchAdminCertifications,
  reorderCertifications,
  updateCertification,
} from '../adminService';
import { useAdminSession } from '../adminSession';
import { ApiError, apiUrl } from '../../../services/apiClient';
import { AdminCertification, CertificationInput } from '../../Company/certificationService';
import formStyles from '../Portfolio/AdminPortfolioForm.module.scss';
import listStyles from '../Portfolio/AdminPortfolio.module.scss';
import styles from './AdminCertifications.module.scss';

const EMPTY_FORM: CertificationInput = {
  title: '',
  issuer: '',
  issuedDate: '',
  isPublished: true,
};

// 회사소개 > 인증(/company/certification)에 노출되는 인증서 관리
function AdminCertifications() {
  const [session, setSession] = useAdminSession();
  const [items, setItems] = useState<AdminCertification[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  // null: 편집 안 함, 'new': 새 인증서, 숫자: 해당 id 수정
  const [editing, setEditing] = useState<number | 'new' | null>(null);
  const [form, setForm] = useState<CertificationInput>(EMPTY_FORM);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const handleError = (err: unknown, fallback: string) => {
    if (err instanceof ApiError && err.status === 401) {
      setSession(null);
    } else {
      setError(err instanceof Error ? err.message : fallback);
    }
  };

  const load = () => {
    if (!session) return;
    fetchAdminCertifications(session.token)
      .then(setItems)
      .catch((err) => handleError(err, '인증 목록을 불러오지 못했습니다.'));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [session]);

  useEffect(
    () => () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    },
    [imagePreview],
  );

  const handleImageChange = (file: File | null) => {
    setImageFile(file);
    setImagePreview(file ? URL.createObjectURL(file) : null);
  };

  const startEdit = (item: AdminCertification | null) => {
    setFormError(null);
    handleImageChange(null);
    if (item) {
      setEditing(item.id);
      setExistingImageUrl(item.imageUrl);
      setForm({
        title: item.title,
        issuer: item.issuer,
        issuedDate: item.issuedDate ?? '',
        isPublished: item.isPublished,
      });
    } else {
      setEditing('new');
      setExistingImageUrl(null);
      setForm(EMPTY_FORM);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const setField = <K extends keyof CertificationInput>(key: K, value: CertificationInput[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!session || editing === null) return;
    if (!form.title.trim() || !form.issuer.trim()) {
      setFormError('인증명과 발급기관을 입력해주세요.');
      return;
    }
    if (editing === 'new' && !imageFile) {
      setFormError('인증서 이미지를 첨부해주세요.');
      return;
    }

    setFormError(null);
    setIsBusy(true);
    try {
      const saved =
        editing === 'new'
          ? await createCertification(session.token, form, imageFile as File)
          : await updateCertification(session.token, editing, form, imageFile);
      setItems((prev) =>
        editing === 'new' ? [saved, ...(prev ?? [])] : (prev ?? []).map((item) => (item.id === saved.id ? saved : item)),
      );
      setEditing(null);
      handleImageChange(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setSession(null);
      } else {
        setFormError(err instanceof Error ? err.message : '저장 중 오류가 발생했습니다.');
      }
    } finally {
      setIsBusy(false);
    }
  };

  const run = async (action: () => Promise<unknown>) => {
    setError(null);
    setIsBusy(true);
    try {
      await action();
    } catch (err) {
      handleError(err, '처리 중 오류가 발생했습니다.');
    } finally {
      setIsBusy(false);
    }
  };

  const handleMove = (index: number, offset: -1 | 1) => {
    if (!session || !items) return;
    const ids = items.map((item) => item.id);
    const target = index + offset;
    [ids[index], ids[target]] = [ids[target], ids[index]];
    run(async () => setItems(await reorderCertifications(session.token, ids)));
  };

  const handleDelete = (item: AdminCertification) => {
    if (!session) return;
    if (!window.confirm(`"${item.title}" 인증을 삭제하시겠습니까? 되돌릴 수 없습니다.`)) return;
    run(async () => {
      await deleteCertification(session.token, item.id);
      setItems((prev) => (prev ?? []).filter((i) => i.id !== item.id));
      if (editing === item.id) setEditing(null);
    });
  };

  const previewUrl = imagePreview ?? (existingImageUrl ? apiUrl(existingImageUrl) : null);

  return (
    <AdminLayout title="인증 관리">
      {editing !== null && (
        <div className="section">
          <div className="sec-head">
            <div className="sec-title">{editing === 'new' ? '새 인증 등록' : '인증 수정'}</div>
            <div className="sec-actions">
              <button type="button" className="btn btn-outline" onClick={() => setEditing(null)}>
                취소
              </button>
            </div>
          </div>

          <form className={formStyles.form} onSubmit={handleSubmit}>
            <div className="card">
              <div className={formStyles.grid}>
                <div className={formStyles.field}>
                  <label>
                    인증명<span className={formStyles.required}>*</span>
                  </label>
                  <input
                    value={form.title}
                    maxLength={200}
                    onChange={(e) => setField('title', e.target.value)}
                    placeholder="예: 사회적기업 인증"
                  />
                </div>
                <div className={formStyles.field}>
                  <label>
                    발급기관<span className={formStyles.required}>*</span>
                  </label>
                  <input
                    value={form.issuer}
                    maxLength={100}
                    onChange={(e) => setField('issuer', e.target.value)}
                    placeholder="예: 고용노동부"
                  />
                </div>
                <div className={formStyles.field}>
                  <label>발급일</label>
                  <input
                    value={form.issuedDate}
                    maxLength={30}
                    onChange={(e) => setField('issuedDate', e.target.value)}
                    placeholder="예: 2020.05.07"
                  />
                </div>
                <div className={formStyles.field}>
                  <label className={formStyles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={form.isPublished}
                      onChange={(e) => setField('isPublished', e.target.checked)}
                    />
                    인증 페이지에 공개
                  </label>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-title" style={{ marginBottom: 12 }}>
                인증서 이미지{editing === 'new' && <span className={formStyles.required}>*</span>}
              </div>
              {previewUrl && <img src={previewUrl} alt="미리보기" className={styles.imagePreview} />}
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.gif,.webp"
                onChange={(e) => handleImageChange(e.target.files?.[0] ?? null)}
              />
              <p className={styles.hint}>
                jpg · png · gif · webp, 최대 20MB. {editing !== 'new' && '새 파일을 선택하면 기존 이미지가 교체됩니다.'}
              </p>
            </div>

            {formError && (
              <div className="card" style={{ color: '#B91C1C' }}>
                {formError}
              </div>
            )}

            <div className={formStyles.submitRow}>
              <button type="submit" className="btn btn-primary" disabled={isBusy}>
                {isBusy ? '저장 중...' : editing === 'new' ? '등록하기' : '저장하기'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="section">
        <div className="sec-head">
          <div className="sec-title">인증 항목 {items?.length ?? 0}건</div>
          <div className="sec-actions">
            <button type="button" className="btn btn-primary" onClick={() => startEdit(null)}>
              + 새 인증 등록
            </button>
          </div>
        </div>

        {error && (
          <div className="card" style={{ color: '#B91C1C', marginBottom: 16 }}>
            {error}
          </div>
        )}

        <div className="card" style={{ padding: 0 }}>
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>순서</th>
                  <th>이미지</th>
                  <th>인증명</th>
                  <th>발급기관</th>
                  <th>발급일</th>
                  <th>공개</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {!items && (
                  <tr>
                    <td colSpan={7} className="emptyRow">
                      불러오는 중...
                    </td>
                  </tr>
                )}
                {items && items.length === 0 && (
                  <tr>
                    <td colSpan={7} className="emptyRow">
                      등록된 인증이 없습니다.
                    </td>
                  </tr>
                )}
                {items?.map((item, index) => (
                  <tr key={item.id} onClick={() => startEdit(item)}>
                    <td onClick={(event) => event.stopPropagation()}>
                      <div className={listStyles.rowActions}>
                        <button
                          type="button"
                          className="btn btn-outline"
                          aria-label="위로"
                          disabled={isBusy || index === 0}
                          onClick={() => handleMove(index, -1)}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline"
                          aria-label="아래로"
                          disabled={isBusy || index === items.length - 1}
                          onClick={() => handleMove(index, 1)}
                        >
                          ↓
                        </button>
                      </div>
                    </td>
                    <td>
                      <img src={apiUrl(item.imageUrl)} alt={item.title} className={styles.thumb} />
                    </td>
                    <td className={styles.titleCell}>{item.title}</td>
                    <td>{item.issuer}</td>
                    <td>{item.issuedDate ?? '-'}</td>
                    <td>
                      <span className={`badgePill ${item.isPublished ? 'ok' : 'neutral'}`}>
                        <span className="pulse"></span> {item.isPublished ? '공개' : '비공개'}
                      </span>
                    </td>
                    <td onClick={(event) => event.stopPropagation()}>
                      <div className={listStyles.rowActions}>
                        <button type="button" className="btn btn-outline" onClick={() => startEdit(item)}>
                          수정
                        </button>
                        <button
                          type="button"
                          className={`btn btn-outline ${listStyles.dangerBtn}`}
                          disabled={isBusy}
                          onClick={() => handleDelete(item)}
                        >
                          삭제
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <p className={styles.hint} style={{ marginTop: 10 }}>
          순서는 회사소개 &gt; 인증 페이지에 보이는 순서입니다.
        </p>
      </div>
    </AdminLayout>
  );
}

export default AdminCertifications;
